/**
 * Appointment Form Modal
 * Create or edit appointments with conflict detection
 */

import { useState } from 'react';
import { usePatientsQuery } from '../../hooks/usePatients';
import {
  useCreateAppointment,
  useUpdateAppointment,
  useAvailableSlotsQuery,
  type CreateAppointmentInput,
  type AppointmentWithDetails,
} from '../../hooks/useAppointments';
import { combineDateAndTime, getDateFromISO, getTimeFromISO } from '../../utils/calendar';
import type { AppointmentStatus } from '../../types';

interface AppointmentFormProps {
  appointment?: AppointmentWithDetails;
  initialDate?: string;
  initialTime?: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function AppointmentForm({
  appointment,
  initialDate,
  initialTime,
  onClose,
  onSuccess,
}: AppointmentFormProps) {
  const isEditing = !!appointment;

  // Form state
  const [patientId, setPatientId] = useState<number | undefined>(appointment?.patient_id);
  const [providerId] = useState<number | undefined>(appointment?.provider_id);
  const [date, setDate] = useState(
    initialDate || (appointment ? getDateFromISO(appointment.appointment_datetime) : '')
  );
  const [time, setTime] = useState(
    initialTime || (appointment ? getTimeFromISO(appointment.appointment_datetime) : '')
  );
  const [duration, setDuration] = useState(appointment?.duration_minutes || 30);
  const [appointmentType, setAppointmentType] = useState(appointment?.appointment_type || '');
  const [reason, setReason] = useState(appointment?.reason || '');
  const [notes, setNotes] = useState(appointment?.notes || '');
  const [status, setStatus] = useState<AppointmentStatus>(appointment?.status || 'scheduled');

  // Search state
  const [patientSearch, setPatientSearch] = useState('');

  // Queries and mutations
  const { data: patientsData } = usePatientsQuery({ search: patientSearch, limit: 50 });
  const createMutation = useCreateAppointment();
  const updateMutation = useUpdateAppointment(appointment?.id || 0);

  // Available slots query (for conflict detection)
  const slotsParams =
    date && time && duration
      ? {
          provider_id: providerId,
          date,
          duration_minutes: duration,
          exclude_appointment_id: appointment?.id,
        }
      : null;

  const { data: availableSlots } = useAvailableSlotsQuery(slotsParams);

  // Check if selected slot is available
  const isSlotAvailable =
    availableSlots?.find((slot) => {
      const slotTime = getTimeFromISO(slot.start);
      return slotTime === time && slot.available;
    }) !== undefined;

  const hasConflict = date && time && availableSlots && !isSlotAvailable;

  // Handle submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!patientId || !date || !time || !appointmentType) {
      alert('Vinsamlegast fylltu út alla nauðsynlega reiti');
      return;
    }

    if (hasConflict) {
      const confirmOverride = window.confirm(
        'Þessi tími er ekki laus. Viltu samt sem áður bóka þennan tíma?'
      );
      if (!confirmOverride) return;
    }

    const appointmentData: CreateAppointmentInput = {
      patient_id: patientId,
      provider_id: providerId,
      appointment_datetime: combineDateAndTime(date, time),
      duration_minutes: duration,
      appointment_type: appointmentType,
      reason: reason || undefined,
      notes: notes || undefined,
      status,
    };

    try {
      if (isEditing) {
        await updateMutation.mutateAsync(appointmentData);
      } else {
        await createMutation.mutateAsync(appointmentData);
      }
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to save appointment:', error);
      alert('Villa kom upp við að vista tímabókun');
    }
  };

  const patients = patientsData?.data || [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              {isEditing ? 'Breyta tímabókun' : 'Ný tímabókun'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {/* Patient Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sjúklingur <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="Leita að sjúklingi..."
              value={patientSearch}
              onChange={(e) => setPatientSearch(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {patients.length > 0 && (
              <div className="mt-2 max-h-40 overflow-y-auto border border-gray-200 rounded-md">
                {patients.map((patient) => (
                  <button
                    key={patient.id}
                    type="button"
                    onClick={() => {
                      setPatientId(patient.id);
                      setPatientSearch(`${patient.first_name} ${patient.last_name}`);
                    }}
                    className={`w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors ${
                      patientId === patient.id ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="font-medium">
                      {patient.first_name} {patient.last_name}
                    </div>
                    {patient.kennitala && (
                      <div className="text-sm text-gray-500">{patient.kennitala}</div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Date and Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Dagsetning <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tími <span className="text-red-500">*</span>
              </label>
              <input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                step="900"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Conflict Warning */}
          {hasConflict && (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-yellow-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-yellow-700">
                    <strong>Viðvörun:</strong> Þessi tími er ekki laus. Annar tími er þegar bókaður
                    á þessum tíma.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Duration */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Lengd (mínútur) <span className="text-red-500">*</span>
            </label>
            <select
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value={15}>15 mín</option>
              <option value={30}>30 mín</option>
              <option value={45}>45 mín</option>
              <option value={60}>1 klst</option>
              <option value={90}>1.5 klst</option>
              <option value={120}>2 klst</option>
              <option value={180}>3 klst</option>
            </select>
          </div>

          {/* Appointment Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tegund tíma <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={appointmentType}
              onChange={(e) => setAppointmentType(e.target.value)}
              placeholder="t.d. Skoðun, Tannhreinsun, Fyllingar"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ástæða heimsóknar</label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Valfrjálst"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Athugasemdir</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Valfrjálst"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Staða</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as AppointmentStatus)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="scheduled">Áætlaður</option>
              <option value="confirmed">Staðfestur</option>
              <option value="arrived">Mættur</option>
              <option value="in_progress">Í gangi</option>
              <option value="completed">Lokið</option>
              <option value="cancelled">Afbókað</option>
              <option value="no_show">Mætti ekki</option>
            </select>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Hætta við
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending || updateMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {createMutation.isPending || updateMutation.isPending
                ? 'Vistar...'
                : isEditing
                  ? 'Vista breytingar'
                  : 'Bóka tíma'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
